import { get } from "@/infra/rest";
import { APIEndpoints } from "@/shared/constants/Endpoints";
import { QueryKeys } from "@/shared/constants/QueryKeys";
import { addNotification } from "@/shared/states/notification";
import { useQuery } from "@tanstack/react-query";
import { ZodError } from "zod";
import { TPRASummaryResponse } from "../types/summary";
import { TPRASummaryResponseSchema } from "../validation/summarySchema";

export const getTPRASummary = async (): Promise<TPRASummaryResponse> => {
  const response = await get<TPRASummaryResponse>({
    url: APIEndpoints.TPRASummary,
  });

  // Runtime validation with Zod - graceful degradation approach
  try {
    const validated = TPRASummaryResponseSchema.parse(response);
    return validated as TPRASummaryResponse;
  } catch (error) {
    if (error instanceof ZodError) {
      // Log validation errors but don't break the dashboard
      console.error("TPRA Summary validation error:", error.issues);
      console.warn("Schema mismatch detected. Using unvalidated response to prevent dashboard failure.");
      console.warn("Response schema mismatch details:", {
        receivedFields: Object.keys(response || {}),
        validationErrors: error.issues.map(issue => ({
          path: issue.path.join("."),
          message: issue.message,
          code: issue.code,
        })),
      });

      // TODO: Report to Bugsnag for production monitoring
      // Bugsnag?.notify(new Error("TPRA Summary Schema Validation Failed"), (event) => {
      //   event.severity = "warning";
      //   event.addMetadata("validation", {
      //     issues: error.issues,
      //     receivedFields: Object.keys(response || {}),
      //   });
      // });

      // Return unvalidated response to allow dashboard to function with partial data
      return response as TPRASummaryResponse;
    }
    // Re-throw non-validation errors (network, parsing, etc.)
    throw error;
  }
};

export const useGetTPRASummary = () => {
  return useQuery<TPRASummaryResponse>({
    queryKey: [QueryKeys.TPRASummary],
    queryFn: async () => {
      try {
        const response = await getTPRASummary();
        return response;
      } catch (error) {
        console.error("Error fetching TPRA summary:", error);

        // User-friendly error message
        addNotification({
          type: "error",
          title: "Error",
          message: "Failed to load TPRA dashboard summary. Please try again.",
        });
        throw error;
      }
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
};

// Export Risks API functions
export { getRisks, useGetRisks } from './getRisks';
